<svelte:options runes={false} />
<script>
  // The TWAP policy section shared by the manual ticket (TradingPanel) and, in PR-B, the strategy
  // deploy form (TASK-20260823-twap-strategies-tca AC 25/26). Core knobs up front (toggle, duration,
  // slice interval, slippage cap, optional symbol allow-list as a chip-toggle list over the caller's
  // symbols); the full pricing surface (D2) behind an "Advanced" disclosure. The policy object is the
  // single source of truth — every edit replaces it immutably so the parent's `bind:policy` reacts;
  // validation stays in lib/twapPolicy.js and the parent passes `errors`/`warnings` back in.
  import {
    DEFAULT_POLICY, DEFAULT_LADDER, POLICY_BOUNDS, PRICE_BASES, POSTURE_LABELS, POSTURES,
    describePolicy, sliceCount,
  } from '../../../lib/twapPolicy.js';

  export let policy = { ...DEFAULT_POLICY };
  /** The deployment's symbols the allow-list may pick from; empty = no allow-list UI (manual ticket). */
  export let symbols = [];
  export let errors = {};
  export let warnings = {};
  export let showToggle = true;
  export let showConcurrency = false;
  export let disabled = false;
  export let idPrefix = 'twap';

  const postureOptions = Object.entries(POSTURE_LABELS).map(([short, label]) => ({ short: Number(short), label }));

  function set(field, value) {
    policy = { ...policy, [field]: value };
  }

  function setNumber(field, raw) {
    const v = raw === '' || raw === null ? null : Number(raw);
    set(field, v === null || Number.isNaN(v) ? null : v);
  }

  function toggleSymbol(sym) {
    const cur = new Set(policy.symbols ?? []);
    if (cur.has(sym)) cur.delete(sym); else cur.add(sym);
    set('symbols', symbols.filter((s) => cur.has(s)));
  }

  function setLadder(index, patch) {
    const ladder = (policy.pricingStrategySequence ?? []).map((s, i) => (i === index ? { ...s, ...patch } : { ...s }));
    set('pricingStrategySequence', ladder);
  }
  function addLadderStep() {
    const ladder = (policy.pricingStrategySequence ?? []).map((s) => ({ ...s }));
    const last = ladder[ladder.length - 1];
    // Insert before the catch-all (101) step, halfway between the previous step and it.
    const prev = ladder.length >= 2 ? ladder[ladder.length - 2].pctTime : 0;
    const step = { pctTime: Math.min(100, Math.round((prev + 100) / 2)), posture: last?.posture ?? POSTURES.MatchMarketSize };
    if (last && last.pctTime === 101) ladder.splice(ladder.length - 1, 0, step); else ladder.push(step);
    set('pricingStrategySequence', ladder);
  }
  function removeLadderStep(index) {
    set('pricingStrategySequence', (policy.pricingStrategySequence ?? []).filter((_, i) => i !== index));
  }
  function resetLadder() {
    set('pricingStrategySequence', DEFAULT_LADDER.map((s) => ({ ...s })));
  }

  $: enabled = !!policy?.enabled;
  $: summary = describePolicy(policy);
  $: slices = sliceCount(policy);
  $: allSymbols = !(policy?.symbols?.length > 0);
  const id = (f) => `${idPrefix}-${f}`;
</script>

<fieldset class="tpf" class:tpf-off={!enabled} {disabled}>
  <legend class="tpf-legend">
    {#if showToggle}
      <label class="tpf-toggle">
        <input type="checkbox" checked={enabled} on:change={(e) => set('enabled', e.currentTarget.checked)} />
        <span>TWAP</span>
      </label>
    {:else}
      <span>TWAP</span>
    {/if}
    <span class="tpf-summary" aria-live="polite">{summary}</span>
  </legend>

  {#if enabled}
    <div class="tpf-grid">
      <div class="tpf-field">
        <label for={id('duration')}>Duration (min)</label>
        <input id={id('duration')} type="number" inputmode="numeric" class="form-input tpf-num"
          min={POLICY_BOUNDS.durationMinutes[0]} max={POLICY_BOUNDS.durationMinutes[1]} step="1"
          value={policy.durationMinutes ?? ''} on:input={(e) => setNumber('durationMinutes', e.currentTarget.value)}
          aria-invalid={!!errors.durationMinutes} aria-describedby={errors.durationMinutes ? id('duration-err') : undefined} />
        {#if errors.durationMinutes}<span class="tpf-err" id={id('duration-err')}>{errors.durationMinutes}</span>{/if}
      </div>
      <div class="tpf-field">
        <label for={id('interval')}>Slice interval (s)</label>
        <input id={id('interval')} type="number" inputmode="numeric" class="form-input tpf-num"
          min={POLICY_BOUNDS.sliceIntervalSeconds[0]} max={POLICY_BOUNDS.sliceIntervalSeconds[1]} step="1"
          value={policy.sliceIntervalSeconds ?? ''} on:input={(e) => setNumber('sliceIntervalSeconds', e.currentTarget.value)}
          aria-invalid={!!errors.sliceIntervalSeconds} aria-describedby={errors.sliceIntervalSeconds ? id('interval-err') : undefined} />
        {#if errors.sliceIntervalSeconds}<span class="tpf-err" id={id('interval-err')}>{errors.sliceIntervalSeconds}</span>
        {:else if slices !== null}<span class="tpf-hint">{slices} slice{slices === 1 ? '' : 's'}</span>{/if}
      </div>
      <div class="tpf-field">
        <label for={id('cap')}>Slippage cap (%)</label>
        <input id={id('cap')} type="number" inputmode="decimal" class="form-input tpf-num"
          min={POLICY_BOUNDS.slippageCapPct[0]} max={POLICY_BOUNDS.slippageCapPct[1]} step="0.1"
          value={policy.slippageCapPct ?? ''} on:input={(e) => setNumber('slippageCapPct', e.currentTarget.value)}
          aria-invalid={!!errors.slippageCapPct} aria-describedby={errors.slippageCapPct ? id('cap-err') : undefined} />
        {#if errors.slippageCapPct}<span class="tpf-err" id={id('cap-err')}>{errors.slippageCapPct}</span>
        {:else}<span class="tpf-hint">0 = off</span>{/if}
      </div>
      {#if showConcurrency}
        <div class="tpf-field">
          <label for={id('parents')}>Max concurrent parents</label>
          <input id={id('parents')} type="number" inputmode="numeric" class="form-input tpf-num" min="1" step="1"
            value={policy.maxConcurrentParents ?? ''} on:input={(e) => setNumber('maxConcurrentParents', e.currentTarget.value)}
            aria-invalid={!!errors.maxConcurrentParents} />
          {#if errors.maxConcurrentParents}<span class="tpf-err">{errors.maxConcurrentParents}</span>{/if}
        </div>
      {/if}
    </div>

    {#if symbols.length > 0}
      <div class="tpf-symbols" role="group" aria-label="TWAP symbol allow-list">
        <span class="tpf-label">Apply to <span class="tpf-hint">({allSymbols ? 'all symbols' : `${policy.symbols.length} of ${symbols.length}`})</span></span>
        <div class="tpf-chips">
          {#each symbols as sym (sym)}
            <button type="button" class="tpf-chip" class:on={policy.symbols?.includes(sym)}
              aria-pressed={policy.symbols?.includes(sym)} on:click={() => toggleSymbol(sym)}>{sym}</button>
          {/each}
        </div>
        {#if errors.symbols}<span class="tpf-err">{errors.symbols}</span>{/if}
      </div>
    {/if}

    <details class="tpf-advanced">
      <summary>Advanced pricing</summary>
      <div class="tpf-grid">
        <div class="tpf-field">
          <label for={id('basis')}>Price basis</label>
          <select id={id('basis')} class="form-select" value={policy.priceBasis} on:change={(e) => set('priceBasis', e.currentTarget.value)}>
            {#each PRICE_BASES as b}<option value={b}>{b}</option>{/each}
          </select>
          {#if errors.priceBasis}<span class="tpf-err">{errors.priceBasis}</span>{/if}
        </div>
        <div class="tpf-field">
          <label for={id('depth')}>Book depth</label>
          <input id={id('depth')} type="number" class="form-input tpf-num" min={POLICY_BOUNDS.maxBookDepth[0]} max={POLICY_BOUNDS.maxBookDepth[1]} step="1"
            value={policy.maxBookDepth ?? ''} on:input={(e) => setNumber('maxBookDepth', e.currentTarget.value)} aria-invalid={!!errors.maxBookDepth} />
          {#if errors.maxBookDepth}<span class="tpf-err">{errors.maxBookDepth}</span>{/if}
        </div>
        <div class="tpf-field">
          <label for={id('tick')}>Tick step</label>
          <input id={id('tick')} type="number" class="form-input tpf-num" min="1" max="20" step="1"
            value={policy.tickStepSize ?? ''} on:input={(e) => setNumber('tickStepSize', e.currentTarget.value)} aria-invalid={!!errors.tickStepSize} />
          {#if errors.tickStepSize}<span class="tpf-err">{errors.tickStepSize}</span>{/if}
        </div>
        <div class="tpf-field">
          <label for={id('rtick')}>Reverse tick step</label>
          <input id={id('rtick')} type="number" class="form-input tpf-num" min="1" max="20" step="1"
            value={policy.reverseTickStepSize ?? ''} on:input={(e) => setNumber('reverseTickStepSize', e.currentTarget.value)} aria-invalid={!!errors.reverseTickStepSize} />
          {#if errors.reverseTickStepSize}<span class="tpf-err">{errors.reverseTickStepSize}</span>{/if}
        </div>
        <div class="tpf-field">
          <label for={id('aggr')}>Aggressive below (% behind)</label>
          <input id={id('aggr')} type="number" class="form-input tpf-num" min="-20" max="20" step="1"
            value={policy.thresholdForAggressivePricing ?? ''} on:input={(e) => setNumber('thresholdForAggressivePricing', e.currentTarget.value)}
            aria-invalid={!!errors.thresholdForAggressivePricing} />
          {#if errors.thresholdForAggressivePricing}<span class="tpf-err">{errors.thresholdForAggressivePricing}</span>{/if}
        </div>
        <div class="tpf-field">
          <label for={id('cons')}>Conservative above (% ahead)</label>
          <input id={id('cons')} type="number" class="form-input tpf-num" min="-20" max="20" step="1"
            value={policy.thresholdForConservativePricing ?? ''} on:input={(e) => setNumber('thresholdForConservativePricing', e.currentTarget.value)}
            aria-invalid={!!errors.thresholdForConservativePricing} />
          {#if errors.thresholdForConservativePricing}<span class="tpf-err">{errors.thresholdForConservativePricing}</span>{/if}
        </div>
      </div>

      <label class="tpf-toggle tpf-ahead">
        <input type="checkbox" checked={policy.doNotTradeAheadOfSchedule !== false}
          on:change={(e) => set('doNotTradeAheadOfSchedule', e.currentTarget.checked)} />
        <span>Do not trade ahead of schedule</span>
      </label>
      {#if warnings.doNotTradeAheadOfSchedule}<p class="tpf-warn" role="status">⚠ {warnings.doNotTradeAheadOfSchedule}</p>{/if}

      <div class="tpf-ladder">
        <div class="tpf-ladder-head">
          <span class="tpf-label">Posture ladder <span class="tpf-hint">(% of schedule elapsed → posture; escalates when behind)</span></span>
          <span class="tpf-ladder-actions">
            <button type="button" class="btn btn-ghost btn-sm" on:click={addLadderStep}>+ step</button>
            <button type="button" class="btn btn-ghost btn-sm" on:click={resetLadder}>Reset</button>
          </span>
        </div>
        <table class="tpf-ladder-table">
          <thead><tr><th scope="col">Until % time</th><th scope="col">Posture</th><th scope="col"><span class="sr-only">Remove</span></th></tr></thead>
          <tbody>
            {#each policy.pricingStrategySequence ?? [] as step, i}
              <tr>
                <td>
                  <input type="number" class="form-input tpf-num tpf-pct" min="1" max="101" step="1" aria-label={`Step ${i + 1} until % time`}
                    value={step.pctTime} on:input={(e) => setLadder(i, { pctTime: Number(e.currentTarget.value) })} />
                </td>
                <td>
                  <select class="form-select" aria-label={`Step ${i + 1} posture`} value={step.posture}
                    on:change={(e) => setLadder(i, { posture: Number(e.currentTarget.value) })}>
                    {#each postureOptions as o}<option value={o.short}>{o.label}</option>{/each}
                  </select>
                </td>
                <td><button type="button" class="btn btn-ghost btn-sm" aria-label={`Remove step ${i + 1}`} on:click={() => removeLadderStep(i)}>✕</button></td>
              </tr>
            {/each}
          </tbody>
        </table>
        {#if errors.pricingStrategySequence}<span class="tpf-err">{errors.pricingStrategySequence}</span>{/if}
      </div>
    </details>
  {/if}
</fieldset>

<style>
  .tpf {
    border: 1px solid var(--border-secondary, #2a2b2e);
    border-radius: var(--radius-base, 0.5rem);
    padding: var(--spacing-sm, 0.5rem) var(--spacing-md, 0.75rem) var(--spacing-md, 0.75rem);
    margin: 0;
    min-width: 0;
  }
  .tpf-off { padding-bottom: var(--spacing-sm, 0.5rem); }
  .tpf-legend { display: flex; align-items: center; gap: var(--spacing-sm, 0.5rem); padding: 0 var(--spacing-xs, 0.25rem); font-size: var(--font-size-sm, 0.85rem); color: var(--text-secondary, #8a8b8d); }
  .tpf-toggle { display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; color: var(--text-primary, #fff); }
  .tpf-toggle input { accent-color: var(--color-primary, #00d4ff); width: 16px; height: 16px; }
  .tpf-summary { font-variant-numeric: tabular-nums; color: var(--text-secondary, #8a8b8d); }
  .tpf-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: var(--spacing-sm, 0.5rem) var(--spacing-md, 0.75rem); margin-top: var(--spacing-sm, 0.5rem); }
  .tpf-field { display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
  .tpf-field label, .tpf-label { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); text-transform: uppercase; letter-spacing: 0.04em; }
  .tpf-num { font-variant-numeric: tabular-nums; text-align: right; }
  .tpf-pct { width: 5.5rem; }
  .tpf-hint { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); text-transform: none; letter-spacing: 0; }
  .tpf-err { font-size: var(--font-size-xs, 0.7rem); color: var(--color-danger, #ff3b3b); }
  .tpf-warn { margin: 0.4rem 0 0; font-size: var(--font-size-xs, 0.7rem); color: var(--color-warning, #ffaa00); }
  .tpf-symbols { margin-top: var(--spacing-md, 0.75rem); display: flex; flex-direction: column; gap: 0.35rem; }
  .tpf-chips { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .tpf-chip {
    font-size: var(--font-size-xs, 0.7rem); padding: 0.15rem 0.55rem; border-radius: 999px; cursor: pointer;
    background: var(--bg-tertiary, #1a1b1e); border: 1px solid var(--border-secondary, #2a2b2e); color: var(--text-secondary, #8a8b8d);
  }
  .tpf-chip.on { color: var(--text-primary, #fff); border-color: var(--color-primary, #00d4ff); background: var(--color-primary-alpha-10, rgba(0, 212, 255, 0.1)); }
  .tpf-chip:focus-visible, .tpf-advanced summary:focus-visible { outline: 2px solid var(--color-primary, #00d4ff); outline-offset: 1px; }
  .tpf-advanced { margin-top: var(--spacing-md, 0.75rem); border-top: 1px dashed var(--border-secondary, #2a2b2e); padding-top: var(--spacing-sm, 0.5rem); }
  .tpf-advanced summary { cursor: pointer; font-size: var(--font-size-sm, 0.85rem); color: var(--text-secondary, #8a8b8d); }
  .tpf-ahead { margin-top: var(--spacing-md, 0.75rem); font-size: var(--font-size-sm, 0.85rem); }
  .tpf-ladder { margin-top: var(--spacing-md, 0.75rem); }
  .tpf-ladder-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .tpf-ladder-actions { display: inline-flex; gap: 0.3rem; }
  .tpf-ladder-table { width: 100%; border-collapse: collapse; margin-top: 0.4rem; font-size: var(--font-size-sm, 0.85rem); }
  .tpf-ladder-table th { text-align: left; font-weight: var(--font-weight-medium, 500); color: var(--text-secondary, #8a8b8d); font-size: var(--font-size-xs, 0.7rem); padding: 0.2rem 0.3rem; }
  .tpf-ladder-table td { padding: 0.2rem 0.3rem; vertical-align: middle; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
